import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Stack,
  DialogActions,
  alpha,
  Chip,
  Paper,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import Slider from 'react-slick';
import BoltIcon from '@mui/icons-material/Bolt';
import { Add, Favorite } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { ThreeDots } from 'react-loader-spinner';
import { CloseIcon } from '../../../../theme/overrides/CustomIcons';
import { useSelector } from '../../../../redux/store';
import { removeAllProjectsEvent, removeProjectEvent, setBasketModal } from '../../../../redux/slices/donation';
import DonationSuccessModal from '../DonationSuccessModal';
import SimplifiedCardPaymentForm from './SimplifiedCardPaymentForm';
import { RefFormProvider } from '../../../../components/hook-form/FormProvider';
import { PaymentMethod } from './PaymentMethods';
import SimplifiedBankTransferForm from './SimplifiedBankTransferForm';
import SimplifiedGoodsForm from './SimplifiedGoodsForm';
import SimplifiedVolunteerForm from './SimplifiedVolunteerForm';

const COLOR_GREEN = '#00A852';
const COLOR_BLUE = '#008CD9';

export default function SimplifiedGivingBasketModal() {
  const { projects, isModalOpen: open } = useSelector((store) => store.donation);

  const dispatch = useDispatch();

  const [successOpen, setSuccessOpen] = useState(false);
  const [projectDonations, setDonations] = useState([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const carouselRef = useRef(null);
  const actionRef = useRef();
  const resolverRef = useRef();
  const formRef = useRef();
  const [paymentMethod, setPaymentMethod] = useState('online_payment');
  const [page, setPage] = useState(0);
  const [isRecurring, setIsRecurring] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [amount, setAmount] = useState(100);
  const [isDiscretionary, setIsDiscretionary] = useState(false);
  const [didRedirect, setDidRedirect] = useState(false);

  const methods = useForm({ resolver: (values, context, options) => resolverRef.current(values, context, options) });

  useEffect(() => {
    resolverRef.current = yupResolver();
  }, []);

  const settings = {
    initialSlide: 0,
    infinite: false,
    autoplay: false,
    slidesToShow: 1,
    centerMode: false,
    variableWidth: false,
    afterChange: (i) => {
      setPage(i);
    },
    adaptiveHeight: true,
  };

  const { handleSubmit, reset } = methods;

  const location = useLocation();

  useEffect(() => {
    if (projects.length) {
      setDonations(Array.from(projects));
      setIsDiscretionary(false);
    } else {
      setDonations([
        {
          _id: '3a344482-9d1c-48fb-9ad6-1270f638eb9f',
          name: 'Discretionary',
          charityId: '51f5b74f-c4d8-4bcc-8c17-34e3c25eeac8',
          charityName: 'CharityPhilippines.org',
          amount,
        },
      ]);
      setIsDiscretionary(true);
    }
  }, [projects, open]);

  useEffect(() => {
    if (isDiscretionary) {
      setDonations((prev) => {
        const donations = Array.from(prev);
        const donation = { ...donations[0] };
        donation.amount = amount;
        donations[0] = donation;
        return donations;
      });
    }
  }, [amount]);

  const onClose = () => {
    setPage(0);
    setAmount(100);
    reset({});
    resolverRef.current = null;
    actionRef.current = null;
    dispatch(setBasketModal(false));
  };

  const onAddProject = () => {
    if (location.pathname !== '/explore-project') {
      window.location.href = '/explore-project';
    } else {
      dispatch(setBasketModal(false));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleRemoveProject = (project) => {
    const projectName = project?.name || 'this project';
    const isConfirmed = window.confirm(`Remove ${projectName} from your giving basket?`);

    if (isConfirmed) {
      dispatch(removeProjectEvent(project._id));
    }
  };

  return (
    <>
      <Dialog fullWidth maxWidth="lg" open={open}>
        <DialogTitle sx={{ borderBottom: '1px solid #f9f9f9', pb: 2 }}>
          <Grid container>
            <Grid item md={4} mb={{xs: 2, md: 0}}>
              <Typography variant="h5">Giving Basket ({projects.length} project selected)</Typography>
            </Grid>
            <Grid
              container
              item
              md={4}
              position={'relative'}
              justifyContent={'space-between'}
              sx={{ '& > *': { transition: 'all 400ms ease-in-out' } }}
            >
              <Box
                width={'calc(100% - 30px)'}
                height={2}
                bgcolor={'#eee'}
                position={'absolute'}
                top={'50%'}
                left={15}
                sx={{ transform: 'translateY(-50%)', zIndex: 0 }}
              />
              <Box
                width={`calc(${(page / 2) * 100}% - 30px)`}
                height={2}
                bgcolor={COLOR_GREEN}
                position={'absolute'}
                top={'50%'}
                left={15}
                sx={{ transform: 'translateY(-50%)', zIndex: 0 }}
              />
              <Box
                width={30}
                height={30}
                bgcolor={COLOR_GREEN}
                position={'relative'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                borderRadius={30}
              >
                <Typography variant="caption" color={'white'}>
                  1
                </Typography>
              </Box>
              <Box
                width={30}
                height={30}
                bgcolor={page >= 1 ? COLOR_GREEN : '#eee'}
                position={'relative'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                borderRadius={30}
                color={page >= 1 ? 'white' : 'unset'}
              >
                <Typography variant="caption">2</Typography>
              </Box>
              <Box position={'relative'} width={30} height={30}>
                <Favorite
                  sx={{
                    fill: page >= 2 ? COLOR_GREEN : '#eee',
                    fontSize: 30,
                  }}
                  bgcolor={page >= 2 ? COLOR_GREEN : '#eee'}
                  position={'relative'}
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  borderRadius={30}
                  color={page >= 2 ? 'white' : 'unset'}
                />
                <Typography
                  variant="caption"
                  position={'absolute'}
                  top={'45%'}
                  left={'50%'}
                  sx={{ transform: 'translate(-50%, -50%)' }}
                  color={page >= 2 ? 'white' : 'unset'}
                >
                  3
                </Typography>
              </Box>
            </Grid>
            <Grid item md={4} position={'relative'}>
              <IconButton
                aria-label="close"
                onClick={() => {
                  onClose();
                }}
                sx={{
                  position: 'absolute',
                  right: { xs: -5, md: -10 },
                  top: { xs: -50, md: -10 },
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent style={{ overflowX: 'hidden' }}>
          <Slider ref={carouselRef} {...settings}>
            {/* SLIDE 1 */}
            <div>
              <Stack>
                {isDiscretionary ? (
                  <>
                    <Card sx={{ p: 3, mt: 4, border: '1px solid', borderColor: 'grey.300' }}>
                      <Box display={'flex'}>
                        <BoltIcon />
                        <Box>
                          <Typography fontSize={24} fontWeight={600} mb={1} lineHeight={'20px'}>
                            Quick Donate
                          </Typography>
                          <Typography>Discretionary Donation</Typography>

                          <Typography maxWidth={600} fontStyle={'italic'} color={'#aaa'} mt={2} mb={3}>
                            If you are uncertain about which project to support,<strong> CharityPhilippines.Org </strong> will ensure your donation goes where it is needed most.
                          </Typography>
                        </Box>
                      </Box>
                      <Grid container spacing={2} justifyContent="center">
                        {[100, 500, 1000, 3000, 5000].map((amt) => (
                          <Grid item key={amt} xs={12} sm={6} md={2.4}>
                            <Button
                              fullWidth
                              variant="contained"
                              disableElevation
                              sx={{
                                py: 2,
                                borderRadius: 1,
                                textTransform: 'none',
                                bgcolor: amt === amount ? COLOR_BLUE : '#F5F5F5',
                                color: amt === amount ? '#fff' : COLOR_BLUE,
                                fontSize: 18,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                '&:hover': {
                                  bgcolor: amt === amount ? COLOR_BLUE : '#e9f3ff',
                                },
                                boxShadow: 'none',
                              }}
                              onClick={() => setAmount(amt)}
                            >
                              PHP {amt.toLocaleString('en-US')}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    </Card>
                    <Grid container justifyContent={'center'} my={10} position={'relative'}>
                      <Grid item xs={3}>
                        <Box borderTop={'.1px solid'} borderColor={'grey.300'} />
                        <Typography
                          color={'grey.500'}
                          padding={1}
                          paddingX={2}
                          bgcolor={'white'}
                          position={'absolute'}
                          top={'50%'}
                          left={'50%'}
                          sx={{
                            transform: 'translate(-50%, -50%)',
                          }}
                        >
                          or
                        </Typography>
                      </Grid>
                    </Grid>
                    <Box width={'100%'} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                      <Button variant="contained" onClick={onAddProject}>
                        <Add fontSize="small" sx={{ mr: 1 }} /> Add Project
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box p={2} pt={4}>
                    {projectDonations.map((e, i) => (
                      <Card key={e._id} sx={{ p: 2, width: '100%' }}>
                        <Grid container spacing={2} alignItems={'stretch'}>
                          <Grid item xs={12} sm={3}>
                            <img
                              src={e.mainImage}
                              alt={e.name}
                              height={200}
                              style={{ aspectRatio: '4/3', objectFit: 'cover' }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Stack justifyContent={'space-between'} height={'100%'}>
                              <Stack spacing={1} alignItems={'start'}>
                                <Chip color="warning" sx={{ color: 'white' }} label={e.mainCause} />
                                <Typography variant="h6">{e.name}</Typography>
                                <Typography variant="body" color={'#aaa'}>
                                  {e.charityDetails.name}
                                </Typography>
                              </Stack>
                              <Grid container mt={2} gap={1} wrap="wrap">
                                {[100, 500, 1000, 3000, 5000].map((amt) => (
                                  <Grid item key={amt} xs="auto">
                                    <Button
                                      sx={{ p: 1.5, px: 2 }}
                                      variant={e.amount === amt ? 'contained' : 'outlined'}
                                      color="info"
                                      disableElevation
                                      onClick={() => {
                                        setDonations((prev) => {
                                          const donations = [...prev];
                                          const project = { ...donations[i] };
                                          project.amount = amt;
                                          donations[i] = project;
                                          return donations;
                                        });
                                      }}
                                    >
                                      PHP {amt.toLocaleString('en-US')}
                                    </Button>
                                  </Grid>
                                ))}
                              </Grid>
                            </Stack>
                          </Grid>
                          <Grid
                            container
                            item
                            xs={12}
                            sm={3}
                            direction={'column'}
                            justifyContent={'space-between'}
                            alignItems={'end'}
                          >
                            <IconButton
                              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                              onClick={() => {
                                handleRemoveProject(e);
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                            <Box position={'relative'} width={{ xs: '100%', sm: 'auto' }}>
                              <TextField
                                size="small"
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">PHP</InputAdornment>,
                                  inputProps: {
                                    sx: {
                                      pt: '14px',
                                      pb: '14px',
                                      textAlign: 'right',
                                      width: { xs: '100%', md: 150 },
                                    },
                                  },
                                }}
                                fullWidth
                                type="number"
                                value={e.amount}
                                onChange={(e) => {
                                  setDonations((prev) => {
                                    const donations = Array.from(prev);
                                    const project = { ...donations[i] };
                                    project.amount = e.target?.value.length ? parseFloat(e.target.value) : null;
                                    donations[i] = project;
                                    console.log(donations);
                                    return donations;
                                  });
                                }}
                              />
                              <Button
                                variant="contained"
                                color="error"
                                fullWidth
                                sx={{ display: { xs: 'flex', sm: 'none' }, mt: 1 }}
                                onClick={() => {
                                  handleRemoveProject(e);
                                }}
                              >
                                Remove
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Card>
                    ))}
                  </Box>
                )}
              </Stack>
            </div>

            {/* SLIDE 2 */}
            <div>
              <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: 'repeat(4, 1fr)' }}
                gap={2}
                p={{ xs: 2, md: 5 }}
                position={'relative'}
                maxHeight={600}
                overflow="auto"
              >
                <Box
                  gridColumn={{ xs: 'span 1', md: 'span 1' }}
                  alignSelf={'start'}
                  position={{ xs: 'static', md: 'sticky' }}
                  top={0}
                  zIndex={1}
                  bgcolor={'background.paper'}
                  pb={{ xs: 1, md: 0 }}
                >
                  <Stack>
                    <Typography variant="h6">Donation Method</Typography>
                    <Typography variant="caption" color={'#aaa'}>
                      Please choose how you'd like to donate.
                    </Typography>
                    <Box mt={2}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={12}>
                          <PaymentMethod
                            imgSrc="/images/donation/DebitCreditEwallet Icon.png"
                            label="Online Payment"
                            selected={paymentMethod === 'online_payment'}
                            setPaymentMethod={() => setPaymentMethod('online_payment')}
                          />
                        </Grid>
                        <Grid item xs={6} sm={12}>
                          <PaymentMethod
                            imgSrc="/images/donation/Bank Transfer Icon.png"
                            label="Bank Transfer"
                            selected={paymentMethod === 'bank_transfer'}
                            setPaymentMethod={() => setPaymentMethod('bank_transfer')}
                          />
                        </Grid>
                        {/* {!(isDiscretionary || projectDonations.reduce((prev, val) => prev + (val.amount ?? 0), 0)) && (
                          <>
                            <Grid item xs={6} sm={12}>
                              <PaymentMethod
                                imgSrc="/images/donation/Goods Icon.png"
                                label="Goods"
                                selected={paymentMethod === 'goods'}
                                setPaymentMethod={() => setPaymentMethod('goods')}
                              />
                            </Grid>
                            <Grid item xs={6} sm={12}>
                              <PaymentMethod
                                imgSrc="/images/donation/VolunteerService.png"
                                label="Volunteer/Service"
                                selected={paymentMethod === 'volunteer'}
                                setPaymentMethod={() => setPaymentMethod('volunteer')}
                              />
                            </Grid>
                          </>
                        )} */}
                      </Grid>
                    </Box>
                  </Stack>
                </Box>

                <Box gridColumn={{ xs: 'span 1', md: 'span 3' }}>
                  <RefFormProvider
                    ref={formRef}
                    methods={methods}
                    onSubmit={handleSubmit((data) => actionRef.current(data, projectDonations))}
                  >
                    {paymentMethod === 'online_payment' && (
                      <SimplifiedCardPaymentForm
                        projects={projectDonations}
                        actionRef={actionRef}
                        resolverRef={resolverRef}
                        methods={methods}
                        onSuccess={() => {
                          carouselRef.current.slickNext();
                          setPage(2);
                          dispatch(removeAllProjectsEvent());
                        }}
                        setIsRecurring={setIsRecurring}
                        setIsLoading={setIsLoading}
                        isDiscretionary={isDiscretionary}
                        onRedirect={() => { setDidRedirect(true); }}
                      />
                    )}
                    {paymentMethod === 'bank_transfer' && (
                      <SimplifiedBankTransferForm
                        projects={projectDonations}
                        actionRef={actionRef}
                        resolverRef={resolverRef}
                        methods={methods}
                        onSuccess={() => {
                          carouselRef.current.slickNext();
                          setPage(2);
                          dispatch(removeAllProjectsEvent());
                        }}
                        setIsLoading={setIsLoading}
                        isDiscretionary={isDiscretionary}
                      />
                    )}
                    {paymentMethod === 'goods' && (
                      <SimplifiedGoodsForm
                        projects={projectDonations}
                        actionRef={actionRef}
                        resolverRef={resolverRef}
                        methods={methods}
                        onSuccess={() => {
                          carouselRef.current.slickNext();
                          setPage(2);
                          dispatch(removeAllProjectsEvent());
                        }}
                        setIsLoading={setIsLoading}
                        isDiscretionary={isDiscretionary}
                      />
                    )}
                    {paymentMethod === 'volunteer' && (
                      <SimplifiedVolunteerForm
                        projects={projectDonations}
                        actionRef={actionRef}
                        resolverRef={resolverRef}
                        methods={methods}
                        onSuccess={() => {
                          carouselRef.current.slickNext();
                          setPage(2);
                          dispatch(removeAllProjectsEvent());
                        }}
                        setIsLoading={setIsLoading}
                        isDiscretionary={isDiscretionary}
                      />
                    )}
                  </RefFormProvider>
                </Box>
              </Box>
            </div>

            {/* SLIDE 3 */}
            <div>
              <Box display={'flex'} flexDirection={'column'} gap={2} alignItems={'center'}>
                <img
                  src="/images/donation-success.png"
                  alt="success"
                  height={220}
                  style={{ objectFit: 'contain', marginTop: 30 }}
                />
                <Typography variant="h5" fontWeight={400}>
                  Thank you for your generous support!
                </Typography>
                <Typography
                        sx={{
                          fontWeight: 400,
                          width: { xs: '100%', md: 600 },
                          textAlign: 'center',
                        }}
                  >

                  Your donation allows CharityPhilippines to continue its mission of helping communities in need. We
                  appreciate your trust in us.
                </Typography>
                {
                  paymentMethod === 'online_payment' && !isRecurring && !didRedirect ?
                    <Stack spacing={3} alignItems={'center'} my={5}>
                      <ThreeDots height={20} width={40} />
                      <Typography fontWeight={400} width={600} textAlign={'center'}>
                        Redirecting to payment...
                      </Typography>
                    </Stack>
                    :
                    <Button variant="contained" size="large" sx={{ my: 5 }} onClick={() => onAddProject()}>
                      EXPLORE MORE PROJECTS
                    </Button>
                }
              </Box>
            </div>
          </Slider>
        </DialogContent>
        {page < 2 && (
          <DialogActions sx={{ borderTop: '1px solid #f9f9f9' }}>
            <Grid container width={'100%'} alignItems={'center'}>
              <Grid item xs={12} md={6}>
                <Box display={'flex'} gap={2} alignItems={'center'} my={{ xs: 2, md: 0 }}>
                  <Typography variant="body1">Total Donation:</Typography>
                  <Typography variant="h5">
                    PHP{' '}
                    {new Intl.NumberFormat('en-PH', {
                      minimumFractionDigits: 2,
                    }).format(
                      isDiscretionary ? amount : projectDonations?.reduce((prev, b) => prev + (b?.amount ?? 0), 0)
                    )}
                  </Typography>
                </Box>
              </Grid>
              <Grid container item xs={12} md={6} justifyContent={'end'}>
                <Grid container item xs="auto" gap={2} alignItems={'end'}>
                  {page > 0 && (
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => {
                        setPage((prev) => prev - 1);
                        carouselRef.current.slickPrev();
                      }}
                    >
                      Back
                    </Button>
                  )}
                  {isRecurring && page === 1 ? (
                    <div id="paypal-button-container">
                      <LoadingButton fullWidth variant="contained" size="large" type="submit">
                        PROCEED TO PAYMENT
                      </LoadingButton>
                    </div>
                  ) : (
                    <LoadingButton
                      loading={isLoading}
                      variant="contained"
                      size="large"
                      onClick={() => {
                        if (page === 0) {
                          setPage((prev) => prev + 1);
                          carouselRef.current.slickNext();
                        }
                        if (page === 1) {
                          formRef.current.requestSubmit();
                        }
                      }}
                    >
                      {page === 1 ? 'DONATE' : 'PROCEED'}
                    </LoadingButton>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </DialogActions>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // Relocate to top-right
      >
        <Alert onClose={handleSnackbarClose} severity="warning">
          {' '}
          {/* Set severity to 'warning' for orange background */}
          Please note that all donations on our platform are processed exclusively in Philippine Pesos (PHP).
        </Alert>
      </Snackbar>

      <DonationSuccessModal open={successOpen} onClose={() => setSuccessOpen(false)} />
    </>
  );
}
